import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { BentoGrid, BentoGridItem } from '@/components/lib/aceternity/BentoGrid';
import { MagicCard } from '@/components/lib/magicui/MagicCard';
import { BorderBeam } from '@/components/lib/magicui/BorderBeam';
import { useReducedMotion } from '@/hooks/useReducedMotion';

interface PortfolioTile {
  id: string;
  badge: string;
  title: string;
  description: string;
  span?: string;
}

export default function AboutPortfolio() {
  const sectionRef = useRef<HTMLElement>(null);
  const prefersReducedMotion = useReducedMotion();

  // Scroll-based parallax
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });

  // Subtle parallax transforms for different tile groups
  const y1 = useTransform(scrollYProgress, [0, 1], prefersReducedMotion ? [0, 0] : [0, 20]);
  const y2 = useTransform(scrollYProgress, [0, 1], prefersReducedMotion ? [0, 0] : [0, -20]);

  // Portfolio tiles data
  const tiles: PortfolioTile[] = [
    {
      id: '1',
      badge: 'COACHING',
      title: 'Turn a generic offer into a premium authority brand',
      description:
        'Rename, reposition and rebuild so high-intent clients understand the offer immediately and book without scrolling through a wall of text.',
      span: 'lg:col-span-2',
    },
    {
      id: '2',
      badge: 'AGENCY',
      title: 'Turn a do-everything agency into a focused revenue partner',
      description:
        'Refine the domain, offers and messaging so the site speaks to the clients you actually want, not everyone with a budget.',
      span: '',
    },
    {
      id: '3',
      badge: 'DOMAIN LAUNCH',
      title: 'Take a premium domain from parked to working',
      description:
        'Design the brand, build the site and wire the automation so the domain becomes a working front door instead of a parked asset.',
      span: '',
    },
    {
      id: '4',
      badge: 'PRODUCTIZED SERVICE',
      title: 'Turn a vague freelance offer into a clear productized service',
      description:
        'Build a sharp brand narrative and a landing page that makes it obvious what clients are buying, and why it is worth a premium.',
      span: 'lg:col-span-2',
    },
    {
      id: '5',
      badge: 'OPTIMIZATION',
      title: 'Redesign with data, not guesswork',
      description:
        'Use analytics and real customer feedback to tighten the offer, clarify the navigation and shorten the path to booking.',
      span: '',
    },
    {
      id: '6',
      badge: 'SYSTEMS & AI',
      title: 'Give the business a brain and a body',
      description:
        'A workspace with long-term memory so the AI understands the business, and automations that do the routine work. This is the part almost nobody has yet.',
      span: '',
    },
  ];

  // Animation variants
  const sectionVariants = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  const headingVariants = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut',
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const headingItemVariants = {
    hidden: { opacity: 0, y: 10 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: 'easeOut' },
    },
  };

  const gridVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.3,
      },
    },
  };

  const tileVariants = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: 'easeOut' },
    },
  };

  return (
    <motion.section
      ref={sectionRef}
      id="portfolio"
      aria-label="Selected outcomes and portfolio"
      variants={sectionVariants}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.2 }}
      className="relative w-full py-20 md:py-24 bg-slate-950 text-slate-50 overflow-hidden"
    >
      {/* Decorative background elements */}
      <div className="pointer-events-none absolute top-1/4 left-1/4 w-96 h-96 bg-[#0000FF]/10 rounded-full blur-3xl opacity-50" />
      <div className="pointer-events-none absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#0000FF]/10 rounded-full blur-3xl opacity-50" />

      {/* Subtle grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.015]"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgb(148 163 184) 1px, transparent 0)`,
          backgroundSize: '40px 40px',
        }}
      />

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-7xl mx-auto">
          {/* Heading block */}
          <motion.div
            variants={headingVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            className="max-w-3xl mx-auto text-center mb-16 md:mb-20"
          >
            <motion.div variants={headingItemVariants}>
              <p className="text-xs md:text-sm font-semibold tracking-wide text-slate-400 uppercase mb-4">
                DENSITY OF COMPETENCE
              </p>
            </motion.div>

            <motion.div variants={headingItemVariants}>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-slate-50 mb-6">
                From premium domains to functioning service businesses.
              </h2>
            </motion.div>

            <motion.div variants={headingItemVariants}>
              <p className="text-lg md:text-xl text-slate-300 leading-relaxed">
                Six shapes the work usually takes. These are the kinds of problems I am brought in for, not client case studies. My clients ask not to be named.
              </p>
            </motion.div>
          </motion.div>

          {/* Bento Grid with BorderBeam */}
          <BorderBeam className="mb-12 md:mb-16" duration={20} delay={1}>
            <motion.ul
              variants={gridVariants}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.2 }}
            >
              <BentoGrid>
                {tiles.map((tile, index) => {
                  // Alternate parallax groups
                  const yTransform = index % 2 === 0 ? y1 : y2;

                  return (
                    <BentoGridItem key={tile.id} className={tile.span}>
                      <motion.li
                        variants={tileVariants}
                        style={{ y: yTransform }}
                        className="h-full"
                      >
                        <MagicCard className="h-full">
                          <div className="p-6 h-full flex flex-col">
                            <span className="text-xs font-semibold text-[#0000FF] tracking-wide uppercase mb-3">
                              {tile.badge}
                            </span>
                            <h3 className="text-lg md:text-xl font-semibold text-slate-100 mb-3">
                              {tile.title}
                            </h3>
                            <p className="text-sm md:text-base text-slate-300 leading-relaxed">
                              {tile.description}
                            </p>
                          </div>
                        </MagicCard>
                      </motion.li>
                    </BentoGridItem>
                  );
                })}
              </BentoGrid>
            </motion.ul>
          </BorderBeam>

          {/* Micro copy footer */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut', delay: 0.4 }}
            viewport={{ once: true, amount: 0.3 }}
            className="max-w-3xl mx-auto text-center mt-12 md:mt-16"
          >
            <p className="text-base text-slate-400 leading-relaxed">
              You'll see more of this work woven through the{' '}
              <Link
                to="/services"
                className="text-slate-300 underline underline-offset-4 hover:text-blue-300 transition-colors"
              >
                Services
              </Link>{' '}
              and{' '}
              <Link
                to="/domains"
                className="text-slate-300 underline underline-offset-4 hover:text-blue-300 transition-colors"
              >
                Domains
              </Link>{' '}
              pages — the goal is always the same: a domain that looks good, works hard, and pays
              you back.
            </p>
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
}

